import React, { useEffect, useRef } from 'react';

/* 🎆 Soft celebratory fireworks for big days — a fixed, click-through canvas
   behind the content. Rockets rise from the bottom and bloom into pink, gold
   and violet sparks. Skipped entirely under prefers-reduced-motion; pauses
   while the tab is hidden. */

const HUES = [338, 268, 45, 318, 0]; // pink, violet, gold, magenta, white-ish

export default function Fireworks() {
  const canvasRef = useRef(null);
  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (reduced) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');

    let w = 0;
    let h = 0;
    let dpr = 1;
    const size = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = Math.max(1, window.innerWidth);
      h = Math.max(1, window.innerHeight);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    };
    size();
    window.addEventListener('resize', size);

    const rockets = [];
    const sparks = [];
    let nextLaunch = performance.now() + 600;
    let raf = 0;
    let running = true;
    let last = performance.now();

    const explode = (x, y) => {
      const hue = HUES[Math.floor(Math.random() * HUES.length)];
      const n = 70 + Math.floor(Math.random() * 40);
      for (let i = 0; i < n; i += 1) {
        const ang = Math.random() * Math.PI * 2;
        const sp = 0.04 + Math.random() * 0.16;
        sparks.push({
          x, y,
          vx: Math.cos(ang) * sp,
          vy: Math.sin(ang) * sp,
          life: 1100 + Math.random() * 700,
          born: performance.now(),
          hue: hue === 0 ? null : hue + (Math.random() * 24 - 12),
          r: 1 + Math.random() * 1.5,
        });
      }
    };

    const frame = (now) => {
      if (!running) return;
      // self-heal if a resize event was missed or fired in a degenerate state
      if (w !== window.innerWidth || h !== window.innerHeight) size();
      const dt = Math.min(40, now - last);
      last = now;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      if (now >= nextLaunch && rockets.length < 3) {
        rockets.push({
          x: w * (0.15 + Math.random() * 0.7),
          y: h + 8,
          vy: -(0.32 + Math.random() * 0.14),
          targetY: h * (0.18 + Math.random() * 0.3),
          hueSeed: Math.random(),
        });
        nextLaunch = now + 1500 + Math.random() * 1400;
      }

      for (let i = rockets.length - 1; i >= 0; i -= 1) {
        const r = rockets[i];
        r.y += r.vy * dt;
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = '#fff6e8';
        ctx.beginPath();
        ctx.arc(r.x, r.y, 1.8, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 0.35;
        ctx.strokeStyle = '#ffd9a8';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(r.x, r.y);
        ctx.lineTo(r.x, r.y + 16);
        ctx.stroke();
        if (r.y <= r.targetY) {
          explode(r.x, r.y);
          rockets.splice(i, 1);
        }
      }

      ctx.globalCompositeOperation = 'lighter';
      for (let i = sparks.length - 1; i >= 0; i -= 1) {
        const s = sparks[i];
        const age = now - s.born;
        if (age >= s.life) {
          sparks.splice(i, 1);
          continue;
        }
        s.vy += 0.00012 * dt; // gravity
        s.vx *= 0.998;
        s.vy *= 0.998;
        s.x += s.vx * dt;
        s.y += s.vy * dt;
        const fade = 1 - age / s.life;
        ctx.globalAlpha = Math.max(0, fade * 0.9);
        ctx.fillStyle = s.hue == null ? '#fff3e0' : `hsl(${s.hue}, 95%, ${60 + fade * 18}%)`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r * (0.6 + fade * 0.6), 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    const onVis = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else {
        running = true;
        last = performance.now();
        raf = requestAnimationFrame(frame);
      }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', size);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [reduced]);

  if (reduced) return null;
  return <canvas ref={canvasRef} className="aj-fireworks" aria-hidden="true" />;
}
